// src/hooks/useCountUp.js
import { useState, useEffect, useRef } from 'react';

/**
 * Animates a number from its previous value to `target` over `duration` ms.
 * Returns the current in-between value to display.
 *
 * Respects prefers-reduced-motion — jumps straight to the target if set.
 */
export const useCountUp = (target, duration = 500) => {
  const [display, setDisplay] = useState(target ?? 0);
  const fromRef = useRef(target ?? 0);
  const frameRef = useRef(null);

  useEffect(() => {
    const to = Number(target) || 0;
    const from = fromRef.current;

    // Honour reduced-motion — no animation, just set the value.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || from === to) {
      setDisplay(to);
      fromRef.current = to;
      return undefined;
    }

    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);

      // easeOutCubic — fast then settling, feels natural for numbers
      const eased = 1 - (1 - progress) ** 3;
      const value = from + (to - from) * eased;

      setDisplay(value);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);

  return display;
};
